import React from "react";
import axios from "axios";
import { MessageSquare, X, Send } from "lucide-react";
import { DEALER } from "@/data/dealership";
import { useT } from "@/i18n/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function genSessionId() {
  try {
    const existing = sessionStorage.getItem("jp_chat_session");
    if (existing) return existing;
    const id = "sess_" + Math.random().toString(36).slice(2) + Date.now();
    sessionStorage.setItem("jp_chat_session", id);
    return id;
  } catch {
    return "sess_" + Date.now();
  }
}

export default function Chatbot() {
  const { t, lang } = useT();
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([{ role: "assistant", text: t("chat.welcome") }]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const sessionId = React.useMemo(() => genSessionId(), []);
  const scrollRef = React.useRef(null);

  // Update welcome message when language changes (only if only the welcome message exists)
  React.useEffect(() => {
    setMessages((m) => (m.length === 1 ? [{ role: "assistant", text: t("chat.welcome") }] : m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
        lang,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply || "..." }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: `${t("chat.error")} ${DEALER.phone}.` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const quickPrompts = [t("chat.quick1"), t("chat.quick2"), t("chat.quick3")];

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 md:w-16 md:h-16 flex items-center justify-center transition-all shadow-lg ${
          open ? "bg-[#0A0A0A] rotate-90" : "bg-[#EB0A1E] hover:bg-[#CC0011]"
        }`}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageSquare className="w-6 h-6 text-white" />}
        {!open && <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full pulse-dot" />}
      </button>

      {open && (
        <div data-testid="chatbot-panel"
          className="fixed bottom-24 right-4 left-4 md:left-auto md:right-6 z-50 w-auto md:w-[400px] max-h-[70vh] bg-white border border-[#E4E4E7] flex flex-col shadow-2xl">
          <div className="bg-[#0A0A0A] text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#EB0A1E] flex items-center justify-center font-display font-bold text-sm">JP</div>
            <div className="flex-1">
              <div className="font-display font-semibold leading-tight">{t("chat.title")}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full pulse-dot" />
                {t("chat.online")}
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8F8F9]">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}-${i}`}
                className={`max-w-[85%] p-3 text-sm leading-relaxed ${
                  m.role === "user" ? "ml-auto bg-[#0A0A0A] text-white" : "bg-white border border-[#E4E4E7] text-[#0A0A0A]"
                }`}>
                {m.text}
              </div>
            ))}
            {loading && (
              <div className="bg-white border border-[#E4E4E7] p-3 max-w-[85%] inline-flex items-center">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-3 py-2 bg-[#F8F8F9] border-t border-[#E4E4E7] flex flex-wrap gap-1.5">
              {quickPrompts.map((q) => (
                <button key={q} data-testid={`chat-quick-${q.slice(0, 12)}`}
                  onClick={() => setInput(q)}
                  className="text-[11px] bg-white border border-[#E4E4E7] hover:border-[#EB0A1E] hover:text-[#EB0A1E] text-[#52525B] px-2.5 py-1.5 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={sendMessage} className="border-t border-[#E4E4E7] p-3 bg-white flex gap-2">
            <input type="text" data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              className="flex-1 px-3 py-2 text-sm border border-[#E4E4E7] focus:border-[#EB0A1E] focus:outline-none" />
            <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send"
              className="bg-[#EB0A1E] hover:bg-[#CC0011] text-white px-4 disabled:opacity-50 transition-colors">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
